import { createFileRoute, Link } from "@tanstack/react-router";
import { BRAND } from "@/lib/behot";
import { LEGAL_OG_IMAGE, LEGAL_OG_IMAGE_ALT, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/terms")({
  head: () =>
    buildPageMetadata({
      path: "/terms",
      title: "Terms and Conditions | BeHot.Now",
      description:
        "The rules for listing, buying Heat, ranking, edit fees, referral credit, refunds and acceptable use on BeHot.Now. All amounts billed in AED.",
      ogTitle: "Terms and Conditions",
      keywords: [
        "BeHot.Now terms",
        "heat pricing",
        "listing rules",
        "refund policy",
        "payment terms",
        "acceptable use policy",
      ],
      ogDescription: "Listing, Heat, payments and acceptable use on BeHot.Now.",
      image: LEGAL_OG_IMAGE,
      imageAlt: LEGAL_OG_IMAGE_ALT,
      twitterCard: "summary_large_image",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Terms", path: "/terms" },
      ],
    }),
  component: Terms,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-bold">{title}</h2>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function P({ text }: { text: string }) {
  return (
    <>
      {text.split("\n\n").map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </>
  );
}

function Terms() {
  const { t, locale } = useT();
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-4xl font-bold">{t("legal.terms.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("legal.terms.updated")}</p>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        {t("legal.terms.intro", { operator: BRAND.operator, domain: BRAND.domain })}
      </p>

      <Section title={t("legal.terms.s1.title")}>
        <P text={t("legal.terms.s1.body")} />
      </Section>

      <Section title={t("legal.terms.s2.title")}>
        <P text={t("legal.terms.s2.body")} />
      </Section>

      <Section title={t("legal.terms.s3.title")}>
        <P text={t("legal.terms.s3.body")} />
      </Section>

      <Section title={t("legal.terms.s3b.title")}>
        <P text={t("legal.terms.s3b.body")} />
      </Section>

      <Section title={t("legal.terms.s4.title")}>
        <P text={t("legal.terms.s4.body")} />
      </Section>

      <Section title={t("legal.terms.s5.title")}>
        <P text={t("legal.terms.s5.body")} />
      </Section>

      <Section title={t("legal.terms.s6.title")}>
        <P text={t("legal.terms.s6.body")} />
      </Section>

      <Section title={t("legal.terms.s6b.title")}>
        <P text={t("legal.terms.s6b.body")} />
      </Section>

      <Section title={t("legal.terms.s7.title")}>
        <P text={t("legal.terms.s7.body")} />
      </Section>

      <Section title={t("legal.terms.s8.title")}>
        <P text={t("legal.terms.s8.body")} />
      </Section>

      <Section title={t("legal.terms.s9.title")}>
        <P text={t("legal.terms.s9.body")} />
      </Section>

      <Section title={t("legal.terms.s10.title")}>
        <P text={t("legal.terms.s10.body")} />
      </Section>

      <Section title={t("legal.terms.s11.title")}>
        <P text={t("legal.terms.s11.body")} />
      </Section>

      <Section title={t("legal.terms.s12.title")}>
        <P text={t("legal.terms.s12.body")} />
      </Section>

      <Section title={t("legal.terms.s13.title")}>
        <P text={t("legal.terms.s13.body")} />
      </Section>

      <Section title={t("legal.terms.s14.title", { operator: BRAND.operator })}>
        <P text={t("legal.terms.s14.body", { operator: BRAND.operator })} />
      </Section>

      <p className="mt-10 text-sm text-muted-foreground">
        {t("legal.common.seeAlso")}{" "}
        <Link to="/privacy" className="text-primary hover:underline">
          {t("legal.common.privacyPolicy")}
        </Link>{" "}
        {t("legal.common.and")}{" "}
        <Link to="/cookie" className="text-primary hover:underline">
          {t("legal.common.cookiePolicy")}
        </Link>
        .
      </p>

      {locale !== "en" ? (
        <p className="mt-6 text-xs text-muted-foreground">{t("legal.common.englishPrevails")}</p>
      ) : null}
    </div>
  );
}
